import { formatDateDDMMYYYY, getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import NoDocumentsFoundIcon from '../../../assets/images/no-documents-found-icon.png'

import { CustomPaginationComponent } from '../../../components/DataTable/CustomPaginationComponent'
import { Button } from '../../../components/Button/Button'

import { DocumentRecord } from '../../../models/Document'

import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetExternalUserByIdQuery } from '../../../store/externalUser/externalUser'
import { useGetUserByIdQuery } from '../../../store/user/user'
import { useAppSelector } from '../../../store/hooks'

import { useDocumentSearchParams } from '../DocumentUpload/hooks'

import { isEmailAttachmentDocument } from '../utils'

import { DocumentRow } from './DocumentRow'
import { skipToken } from '@reduxjs/toolkit/query'

interface DocumentGroupsProps {
  isLoading: boolean
  groups: Record<string, DocumentRecord[]>
  totalElements: number
  accountHasZDRPolicy: boolean
}

export const DocumentGroups: React.FC<DocumentGroupsProps> = ({ isLoading, groups, totalElements, accountHasZDRPolicy }) => {
  const { page, pageSize, searchTerm, handlePageChange, handlePageSizeChange, handleSearchTermChange } = useDocumentSearchParams()

  const entries = Object.entries(groups)

  const handleClearSearch = () => {
    handleSearchTermChange('')
  }

  return (
    <div className='d-flex flex-column zocks-gap-0.75'>
      {!isLoading ? (
        !entries.length ? (
          <NoDocumentsFound
            showClearButton={searchTerm !== null && searchTerm.length > 0}
            onClear={handleClearSearch}
            accountHasZDRPolicy={accountHasZDRPolicy}
          />
        ) : (
          <>
            {entries.map(([groupId, documents]) => (
              <DocumentGroup key={groupId} documents={documents} />
            ))}
            <CustomPaginationComponent
              rowsPerPage={pageSize}
              rowCount={totalElements}
              currentPage={page}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePageSizeChange}
            />
          </>
        )
      ) : null}
    </div>
  )
}

interface DocumentGroupProps {
  documents: DocumentRecord[]
}

export const DocumentGroup: React.FC<DocumentGroupProps> = ({ documents }) => {
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  const { contactId, createdBy, createdAt } = documents[0]

  const { data: creatorResponse } = useGetUserByIdQuery({ accountId, userId: createdBy.id })
  const { data: contactResponse } = useGetExternalUserByIdQuery(contactId ? { accountId, userId: contactId } : skipToken)

  const creator = creatorResponse?.data ?? null
  const contact = contactResponse?.data ?? null

  const creatorUserDisplayName = creator ? (creator.id === currentUser.id ? 'Me' : getUserDisplayName(creator)) : null

  const isEmailSourced = documents.every(isEmailAttachmentDocument)

  const attribution = isEmailSourced
    ? documents.length === 1
      ? 'From email attachment'
      : `${documents.length} email attachments`
    : creatorUserDisplayName
      ? documents.length === 1
        ? `Uploaded by ${creatorUserDisplayName}`
        : `${documents.length} files by ${creatorUserDisplayName}`
      : null

  return (
    <>
      <div className='d-flex align-items-center justify-content-between gap-3 zocks-pt-0.75'>
        <span className='lead-medium'>{contact ? `${formatDateDDMMYYYY(createdAt)} - ${getUserDisplayName(contact)}` : formatDateDDMMYYYY(createdAt)}</span>
        {attribution ? <span className='paragraph-extra-small zocks-text-gray-700'>{attribution}</span> : null}
      </div>
      {documents.map((document) => (
        <DocumentRow key={document.id} document={document} />
      ))}
    </>
  )
}

interface NoDocumentsFoundProps {
  showClearButton: boolean
  onClear: () => void
  accountHasZDRPolicy: boolean
}

const NoDocumentsFound: React.FC<NoDocumentsFoundProps> = ({ showClearButton, onClear, accountHasZDRPolicy }) => {
  return (
    <div className='d-flex flex-column align-items-center gap-3 zocks-py-2.0 w-100 documents-no-documents-found-container'>
      <img alt='No documents found' src={NoDocumentsFoundIcon} />
      <div className='text-center'>
        <h5>No documents found</h5>
        <span className='paragraph-medium zocks-text-gray-600'>
          {accountHasZDRPolicy
            ? "Your account has zero data retention policy for documents, so Zocks doesn't store any of them."
            : 'Try different search terms or change filtering.'}
        </span>
      </div>
      {showClearButton ? (
        <Button variant='grayGhost' className='button-small px-2 py-1' onClick={onClear}>
          Clear search
        </Button>
      ) : null}
    </div>
  )
}
